import React from 'react';
import { Mail, Phone } from 'lucide-react';
import SEO from '../components/SEO';

const MITCPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="MITC | Most Important Terms & Conditions | Equi Pulse"
        description="Most Important Terms and Conditions (MITC) for Equi Pulse Research advisory services as per SEBI regulations for research analysts in India."
        url="/mitc"
      />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-amber-400 mb-4">Most Important Terms and Conditions (MITC)</h1>
        <p className="text-gray-400 mb-8">(Forming part of the Terms and Conditions for providing research services)</p>
        
        <div className="prose prose-invert prose-amber max-w-none text-gray-300">
          <div className="bg-slate-800/50 border border-amber-500/20 p-6 rounded-lg mb-8">
            <h2 className="text-xl font-bold text-amber-400 mb-3">Clarification</h2>
            <p className="mb-3">
              Throughout this document, "<strong>Equi Pulse</strong>" refers to the SEBI-registered Research Analyst operating under the trading name Equi Pulse, owned by Siddharth Sharma (Proprietor), SEBI Registered Research Analyst (Registration No.: INH000024046).
            </p>
            <p>
              The term "<strong>representative(s)</strong>" refers to any individual acting for or on behalf of Equi Pulse, including staff, authorized persons, or anyone claiming association with Equi Pulse.
            </p>
          </div>

          <div className="space-y-8">
            <div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">1. Research Services</h2>
              <p className="mb-4">
                These terms and conditions, and the consent thereon, relate to the research services provided by Equi Pulse or its representative(s).
              </p>
              <p>
                <strong>Important:</strong> Equi Pulse or its representative(s) cannot execute or carry out any trade (purchase/sell transaction) on behalf of the client. Clients are strictly advised not to permit Equi Pulse or its representative(s) to execute trades on their behalf.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">2. Fee Structure</h2>
              <p className="mb-4">
                The fee charged by Equi Pulse to the client will be subject to the maximum amount prescribed by SEBI/Research Analyst Administration & Supervisory Body (RAASB) from time to time (applicable only for Individual and HUF clients).
              </p>
              <div className="bg-amber-900/20 border border-amber-500/30 p-4 rounded-lg">
                <p className="font-semibold mb-2">Note:</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>The current fee limit is ₹1,51,000/- per annum per family of the client for all research services</li>
                  <li>The fee limit does not include statutory charges</li>
                  <li>The fee limit does not apply to non-individual clients or accredited investors</li>
                </ul>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">3. Advance Fee & Refund</h2>
              <p className="mb-3">
                Equi Pulse may charge fees in advance if mutually agreed with the client. Such advance shall not exceed one quarter (current SEBI stipulation).
              </p>
              <p>
                In case of premature termination of services by either the client or Equi Pulse, the client is entitled to a refund of the proportionate fee for the unexpired period.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">4. Payment Methods</h2>
              <p className="mb-3">
                Fees payable to Equi Pulse may be made only through approved modes such as cheque, online bank transfer (NEFT/RTGS), UPI, etc.
              </p>
              <p className="mb-3">
                <strong className="text-red-400">Cash payments are strictly prohibited.</strong>
              </p>
              <p>
                Clients may also optionally pay via CeFCoM, managed by BSE Limited (currently recognized RAASB).
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">5. Conflict of Interest</h2>
              <p className="mb-3">
                Equi Pulse and its representative(s) are required to comply with all applicable SEBI and RAASB regulations, circulars, and directions regarding disclosure and mitigation of actual or potential conflicts of interest.
              </p>
              <p>
                Equi Pulse will promptly inform the client of any conflict of interest that may affect the services being rendered.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">6. Prohibited Schemes</h2>
              <p>
                Any assured, guaranteed, fixed-return scheme, or any other scheme of similar nature, is prohibited by law. Equi Pulse or its representative(s) does not offer any such scheme to clients.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">7. No Guarantee of Returns</h2>
              <p className="mb-3">
                Equi Pulse or its representative(s) cannot guarantee returns, profits, accuracy, or risk-free outcomes from the use of its research services.
              </p>
              <p>
                All opinions, projections, and estimates are based on available data and reasonable assumptions at the time of preparing/publishing the research report.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">8. Market Risks</h2>
              <p className="mb-3">
                Trading/Investing based on recommendations in research reports of Equi Pulse are subject to market risks.
              </p>
              <p className="mb-3">
                Recommendations do not assure returns, and clients have no recourse to claim losses resulting from decisions based on such research.
              </p>
              <p>
                Reliance on the research is entirely at the client's discretion and judgement.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">9. SEBI Registration Disclaimer</h2>
              <p>
                SEBI registration, RAASB enlistment, and NISM certification do not guarantee the performance of Equi Pulse or its representative(s), nor do they assure any returns.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">10. Grievance Redressal</h2>
              <div className="space-y-4">
                <div className="bg-slate-800/50 border border-emerald-500/20 p-4 rounded-lg">
                  <p className="font-semibold text-emerald-400 mb-2">Step 1: Contact Equi Pulse</p>
                  <p className="mb-2">Clients should first contact Equi Pulse using the details below:</p>
                  <div className="space-y-1 text-sm">
                    <p><strong>Equi Pulse</strong></p>
                    <p>RE-54, Gyansila City, Dhabli, Indore (M.P.) – 453771</p>
                    <p>Email: <a href="mailto:compliance@equipulseresearch.com" className="text-amber-400">compliance@equipulseresearch.com</a></p>
                    <p>Phone: <a href="tel:8982892301" className="text-amber-400">8982892301</a></p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-amber-500/20 p-4 rounded-lg">
                  <p className="font-semibold text-amber-400 mb-2">Step 2: SEBI SCORES</p>
                  <p className="mb-2">If the resolution is unsatisfactory, clients may lodge a complaint at:</p>
                  <a href="https://scores.sebi.gov.in" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">
                    SEBI SCORES Portal (scores.sebi.gov.in)
                  </a>
                </div>

                <div className="bg-slate-800/50 border border-emerald-500/20 p-4 rounded-lg">
                  <p className="font-semibold text-emerald-400 mb-2">Step 3: Online Dispute Resolution</p>
                  <p className="mb-2">Clients may also use the Online Dispute Resolution mechanism:</p>
                  <a href="https://smartodr.in" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">
                    SMART ODR Platform (smartodr.in)
                  </a>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">11. Contact Details</h2>
              <p>
                Clients must keep their contact details, including email ID and mobile number(s), updated with Equi Pulse at all times.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">12. Security Warning</h2>
              <div className="bg-red-900/20 border border-red-500/30 p-4 rounded-lg">
                <p className="font-semibold text-red-400 mb-2">⚠️ Important Security Notice</p>
                <p className="mb-3">
                  Equi Pulse or its representative(s) shall <strong>never</strong> ask for login credentials, passwords, or OTPs of any Trading Account, Demat Account, or Bank Account.
                </p>
                <p>
                  Clients must never share such information with anyone, including anyone claiming to represent Equi Pulse.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MITCPage;